import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout, { PageHero } from '../../components/PublicLayout';
import { Button } from '../../components/ui/button';
import {
  Briefcase, Building2, Receipt, Landmark, Globe2, Users, Sparkles,
  ArrowRight, Check, ShieldCheck, Wallet, BarChart3, FileText, CreditCard,
} from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

const segments = [
  { icon: Briefcase, name: 'Auto-entrepreneurs', desc: 'Compte pro 100% en ligne, ouvert en 5 minutes, sans frais cachés.', to: '/inscription' },
  { icon: Building2, name: 'TPE / PME', desc: 'Compte pro avec cartes équipe, multi-utilisateurs, intégration comptable.', to: '/inscription' },
  { icon: Landmark, name: 'Grandes entreprises', desc: 'Solutions sur mesure, gestion de trésorerie, virements de masse, API.', to: '/inscription' },
  { icon: Users, name: 'Associations', desc: 'Compte associatif simplifié, frais réduits et statuts dédiés.', to: '/inscription' },
];

const offers = [
  {
    id: 'pro-essentiel', name: 'Pro Essentiel', price: '9,90', sub: 'Pour les indépendants',
    style: 'basic',
    features: [
      'Compte pro avec IBAN français',
      'Carte Visa Business',
      'Virements SEPA illimités',
      '50 chèques/an',
      'Encaissement par lien de paiement',
      'Application mobile',
    ],
  },
  {
    id: 'pro-business', name: 'Pro Business', price: '24,90', sub: 'Pour TPE & PME',
    style: 'premium', popular: true,
    features: [
      'Tout Essentiel',
      'Mastercard Business + 3 cartes équipe',
      'Virements SWIFT internationaux',
      'Encaissement carte (TPE)',
      'Intégration comptable (Pennylane, QuickBooks…)',
      'Support prioritaire 24/7',
    ],
  },
  {
    id: 'pro-corporate', name: 'Pro Corporate', price: 'Sur devis', sub: 'Pour grandes entreprises',
    style: 'elite',
    features: [
      'Tout Business',
      'Cartes métal Elite illimitées',
      'Virements SWIFT haute valeur',
      'Multi-comptes / multi-devises',
      'API trésorerie & rapprochement',
      'Conseiller d’affaires dédié',
    ],
  },
];

const services = [
  { icon: Receipt, title: 'Encaissement', desc: 'Lien de paiement, TPE, paiement en ligne, virements clients.' },
  { icon: Globe2, title: 'International', desc: 'Virements SWIFT en USD, GBP, CHF, JPY… Multi-devises.' },
  { icon: BarChart3, title: 'Trésorerie & analytics', desc: 'Pilotez vos flux : tableaux de bord, alertes, prévisionnel.' },
  { icon: FileText, title: 'Comptabilité', desc: 'Export FEC, intégrations Pennylane, QuickBooks, Sage, EBP…' },
  { icon: CreditCard, title: 'Cartes équipe', desc: 'Distribuez des cartes virtuelles ou physiques avec plafonds.' },
  { icon: Wallet, title: 'Crédit pro', desc: 'Prêts professionnels, crédit-bail, affacturage.' },
];

export default function Professionnels() {
  const { t } = useLanguage();
  return (
    <PublicLayout>
      <PageHero
        eyebrow={t('public.proEyebrow')}
        title={t('public.proTitle')}
        subtitle={t('public.proSubtitle')}
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/inscription">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100">
              {t('public.openProAccount')} <ArrowRight className="ml-2 w-4 h-4"/>
            </Button>
          </Link>
          <a href="mailto:pro@bfigroupefr.com">
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              {t('public.contactAdvisor')}
            </Button>
          </a>
        </div>
      </PageHero>

      {/* Segments */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-blue-700 text-sm font-semibold uppercase tracking-widest">Pour qui ?</div>
          <h2 className="text-3xl md:text-4xl font-bold mt-2">Une banque pour chaque profil pro</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {segments.map((s, i) => (
            <Link key={i} to={s.to} className="group p-6 rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-xl transition bg-white">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center group-hover:scale-110 transition">
                <s.icon className="w-6 h-6"/>
              </div>
              <h3 className="font-semibold text-lg mt-4">{s.name}</h3>
              <p className="text-slate-600 text-sm mt-2">{s.desc}</p>
              <div className="mt-3 text-blue-700 text-sm font-medium inline-flex items-center gap-1">
                En savoir plus <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition"/>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Offres tarifaires Pro */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-blue-700 text-sm font-semibold uppercase tracking-widest">Tarifs Pro</div>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Choisissez votre offre pro</h2>
            <p className="text-slate-600 mt-3">Sans engagement, modulable à tout moment, frais transparents.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {offers.map((o) => (
              <div key={o.id} className={`relative rounded-2xl p-7 border ${o.popular ? 'border-blue-700 ring-2 ring-blue-200 shadow-2xl' : 'border-slate-200'} bg-white`}>
                {o.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-700 text-white text-xs font-semibold px-3 py-1 rounded-full">Recommandée</div>}
                <div className={`bfi-card ${o.style} mb-6`} style={{maxWidth:'100%'}}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm uppercase tracking-widest">{o.name}</div>
                    <div className="text-xl font-bold italic">{o.style === 'elite' ? 'mastercard' : 'VISA'}</div>
                  </div>
                  {o.style === 'elite' && (
                    <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 text-[10px] uppercase tracking-widest">
                      <Sparkles className="w-3 h-3"/> Métal noir Business
                    </div>
                  )}
                  <div className="mt-10 font-mono tracking-widest text-sm">•••• •••• •••• 0042</div>
                  <div className="flex justify-between mt-4 text-xs opacity-90">
                    <span>BFI PRO</span><span>12/30</span>
                  </div>
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-900">{o.name}</div>
                  <div className="text-sm text-slate-500">{o.sub}</div>
                  <div className="text-3xl font-bold mt-3 text-blue-900">
                    {o.price === 'Sur devis' ? 'Sur devis' : <>{o.price} €<span className="text-base font-medium text-slate-500">/mois HT</span></>}
                  </div>
                </div>
                <ul className="mt-5 space-y-2 text-sm">
                  {o.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5"/> {f}</li>
                  ))}
                </ul>
                <Link to="/inscription">
                  <Button className={`mt-6 w-full ${o.popular ? 'bg-blue-700 hover:bg-blue-800' : 'bg-slate-900 hover:bg-slate-800'}`}>
                    {o.price === 'Sur devis' ? 'Demander un devis' : `Choisir ${o.name}`}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services pro */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-blue-700 text-sm font-semibold uppercase tracking-widest">Services Pro</div>
          <h2 className="text-3xl md:text-4xl font-bold mt-2">Tous les outils pour gérer votre activité</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {services.map((s, i) => (
            <div key={i} className="p-6 rounded-2xl border border-slate-200 bg-white hover:shadow-lg transition">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                <s.icon className="w-6 h-6"/>
              </div>
              <h3 className="font-semibold text-lg mt-3">{s.title}</h3>
              <p className="text-slate-600 text-sm mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA contact pro */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-blue-900 to-indigo-900 p-10 md:p-14 text-white relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl"></div>
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-medium">
                <ShieldCheck className="w-3 h-3"/> Conseiller pro dédié
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mt-4">Un projet entreprise ? Parlons-en.</h2>
              <p className="text-white/80 mt-3">
                Notre équipe BFI Pro vous accompagne dans le choix de votre offre, l'ouverture de compte, et l'intégration à vos outils.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="mailto:pro@bfigroupefr.com">
                  <Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100">
                    Contacter BFI Pro
                  </Button>
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/10 border border-white/15 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold">+30 000</div>
                <div className="text-xs text-white/70 uppercase mt-1">Pros clients</div>
              </div>
              <div className="bg-white/10 border border-white/15 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold">5 min</div>
                <div className="text-xs text-white/70 uppercase mt-1">Ouverture compte</div>
              </div>
              <div className="bg-white/10 border border-white/15 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold">SEPA + SWIFT</div>
                <div className="text-xs text-white/70 uppercase mt-1">Inclus</div>
              </div>
              <div className="bg-white/10 border border-white/15 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-xs text-white/70 uppercase mt-1">Support pro</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
